import React from 'react';
import { FileText, Calendar } from 'lucide-react';

/**
 * Terms of Service page for the Central Health Provider Portal.
 * Static content — linked from the footer and registration flow.
 */
const TermsOfServicePage = () => {
  const lastUpdated = 'April 1, 2026';

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <FileText size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>Terms of Service</h1>
          </div>
          <p>Please read these terms carefully before using the Central Health Provider Portal</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: '#6B7280', fontSize: 13, fontWeight: 500,
        }}>
          <Calendar size={14} strokeWidth={2} />
          <span>Last updated: {lastUpdated}</span>
        </div>
      </div>

      <div className="card" style={{ lineHeight: 1.65, color: '#374151' }}>
        <h2 style={sectionHeading}>1. Acceptance of Terms</h2>
        <p>
          By accessing and using the Central Health Provider Portal (the "Portal"), you accept
          and agree to be bound by these Terms of Service ("Terms"). If you do not agree
          to these Terms, you must not access or use the Portal. Your use of the Portal
          constitutes an agreement between you and Central Health Plans, Inc. ("Central Health,"
          "we," "us," or "our").
        </p>

        <h2 style={sectionHeading}>2. Eligibility and Account Registration</h2>
        <p>
          Access to the Portal is limited to authorized healthcare providers, their staff,
          and administrative representatives who have a contractual relationship with
          Central Health Plans. You must provide accurate, current, and complete information
          during registration and maintain the accuracy of such information.
        </p>
        <p>
          You are responsible for safeguarding your account credentials and for all
          activities that occur under your account. You agree to notify Central Health immediately
          of any unauthorized use of your account.
        </p>

        <h2 style={sectionHeading}>3. Permitted Use</h2>
        <p>You may use the Portal solely to:</p>
        <ul style={bulletList}>
          <li>Verify patient eligibility and benefits</li>
          <li>Submit and track prior authorization requests</li>
          <li>View claim status and Explanation of Payments (EOPs)</li>
          <li>Access provider resources, forms, and fee schedules</li>
          <li>Manage your provider and organizational information</li>
        </ul>

        <h2 style={sectionHeading}>4. Prohibited Activities</h2>
        <p>You agree NOT to:</p>
        <ul style={bulletList}>
          <li>Share your login credentials with unauthorized individuals</li>
          <li>Attempt to gain unauthorized access to any system, data, or account</li>
          <li>Use the Portal for any unlawful or fraudulent purpose</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Portal</li>
          <li>Interfere with or disrupt the integrity or performance of the Portal</li>
          <li>Transmit viruses, malware, or other harmful code</li>
          <li>Scrape, harvest, or collect data from the Portal in bulk</li>
        </ul>

        <h2 style={sectionHeading}>5. Protected Health Information (PHI)</h2>
        <p>
          The Portal contains Protected Health Information as defined by the Health
          Insurance Portability and Accountability Act of 1996 ("HIPAA"). You acknowledge
          that you have a Business Associate Agreement in place with Central Health and agree
          to use and disclose PHI only as permitted by HIPAA, applicable state law, and
          your provider agreement.
        </p>

        <h2 style={sectionHeading}>6. Intellectual Property</h2>
        <p>
          All content, software, and materials made available through the Portal are
          owned by Central Health Plans or its licensors and are protected by copyright,
          trademark, and other intellectual-property laws. You receive only a limited,
          revocable, non-transferable license to use the Portal for its intended purpose.
        </p>

        <h2 style={sectionHeading}>7. Disclaimer of Warranties</h2>
        <p>
          THE PORTAL IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND,
          EITHER EXPRESS OR IMPLIED. CENTRAL HEALTH DOES NOT WARRANT THAT THE PORTAL WILL BE
          UNINTERRUPTED, ERROR-FREE, OR SECURE.
        </p>

        <h2 style={sectionHeading}>8. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, Central Health shall not be liable
          for any indirect, incidental, special, consequential, or punitive damages
          arising out of or relating to your use of the Portal.
        </p>

        <h2 style={sectionHeading}>9. Termination</h2>
        <p>
          Central Health may suspend or terminate your access to the Portal at any time, with or
          without notice, for any reason, including violation of these Terms. Upon
          termination, your right to use the Portal will immediately cease.
        </p>

        <h2 style={sectionHeading}>10. Changes to These Terms</h2>
        <p>
          We may modify these Terms at any time. Continued use of the Portal following
          notice of any modifications constitutes your acceptance of the revised Terms.
        </p>

        <h2 style={sectionHeading}>11. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Texas, without regard to
          conflict-of-laws principles. Any dispute arising under these Terms shall be
          resolved exclusively in the state or federal courts located in Travis County, Texas.
        </p>

        <h2 style={sectionHeading}>12. Contact</h2>
        <p>
          If you have questions about these Terms, please contact us at
                  {' '}<a href="mailto:chproviderservices@centralhealth.net" style={linkStyle}>chproviderservices@centralhealth.net</a>{' '}
                  or call <strong>512-978-8130</strong>.
        </p>
      </div>
    </div>
  );
};

const sectionHeading = {
  fontSize: 17,
  fontWeight: 700,
  color: '#1A1D21',
  marginTop: 24,
  marginBottom: 10,
};

const bulletList = {
  margin: '8px 0 16px 20px',
  paddingLeft: 4,
};

const linkStyle = {
  color: '#0D9488',
  fontWeight: 600,
  textDecoration: 'none',
};

export default TermsOfServicePage;
